import { useListPayoutPolicies } from "@fp/admin-api-client/payout-policies/payout-policies.client";
import { Badge } from "@fp/ui/components/base/badges/badges";
import { Button } from "@fp/ui/components/base/buttons/button";
import { PageContentContainer } from "@fp/ui/components/base/page-layout/page-layout";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { ChevronRight, Plus } from "@untitledui/icons";
import { AdminLayout } from "@/components/admin-layout";
import { AdminPageHeader } from "@/components/admin-page-header";
import { describeEarlyDisbursement, describeReleaseTiming } from "@/lib/payout-terms";
import { PayoutPolicyHelp } from "./-components/payout-policy-help";
import { redirectUnlessPayoutPolicyEnabled } from "./-components/payout-policy-gate";

export const Route = createFileRoute("/settings/payout-policies/")({
  beforeLoad: ({ context: { queryClient } }) => redirectUnlessPayoutPolicyEnabled(queryClient),
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();

  const { data: policiesResponse } = useListPayoutPolicies();
  const policies = policiesResponse?.data ?? [];
  const maximumFeeRate = policies[0]?.maximumFeeRate ?? 0;

  return (
    <AdminLayout>
      <AdminPageHeader
        title="Payout Policies"
        parent={{ url: "/settings", label: "Settings" }}
        titleActions={<PayoutPolicyHelp maximumFeeRate={maximumFeeRate} />}
      />
      <PageContentContainer>
        <div className="max-w-3xl space-y-4">
          <div className="flex justify-end">
            <Button color="primary" iconLeading={Plus} onClick={() => navigate({ to: "/settings/payout-policies/new" })}>
              New Payout Policy
            </Button>
          </div>
          <div className="space-y-3">
            {policies.map((policy) => (
              <Link
                key={policy.id}
                to="/settings/payout-policies/$id"
                params={{ id: policy.id }}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-4 transition-colors hover:border-gray-300"
              >
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="truncate font-medium text-gray-900">{policy.name}</span>
                    {policy.isDefault && (
                      <Badge size="sm" color="brand">
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {describeReleaseTiming(policy.releaseTiming)} · {describeEarlyDisbursement(policy.earlyDisbursement)}
                  </p>
                </div>
                <ChevronRight className="size-5 shrink-0 text-gray-400" />
              </Link>
            ))}
          </div>
          <p className="text-sm text-gray-500">
            Payees who join the roster receive the default policy&apos;s terms. Changing or deleting a policy changes no payee&apos;s terms. Release Immediately is the standard policy every organization has. It cannot be deleted and becomes the default again when the default is deleted.
          </p>
        </div>
      </PageContentContainer>
    </AdminLayout>
  );
}
